import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, filter, withLatestFrom, tap, mergeMap } from 'rxjs/operators';
import { UsersService } from '@services/users/users.service'
import { UsersActions } from '@actions/index'
import { isNotEmpty } from 'src/app/core/utilities/function-utils';
import * as fromApp from '@reducers/index'
import { SentInvitationModel, BusinessModel, UserModel, UserBusinessModel, ResponseModel, RequestModel, ResellerModel } from '@models/index';
import { HttpApiService } from '@services/http-api.service';
import { MessageService } from 'primeng/api';

@Injectable()
export class UsersEffects {

  searchUsersByBusiness$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.searchUsersByBusiness),
      switchMap(({ request }) =>
        this.httpApiService
          .getRequest(request)
          .pipe(
            map(data => data as ResponseModel),
            map(users => UsersActions.searchAllUsersSuccess({ users })),
            catchError(error => of(UsersActions.usersError({ error: error.message })))
          )
      )
    )
  );

  searchUsersByQueryByBusiness$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.searchUsersByQueryByBusiness),
      switchMap(({ request }) =>
        this.httpApiService
          .getRequest(request)
          .pipe(
            map(data => data as ResponseModel),
            map(data => UsersActions.searchUsersByQuerySuccess({ foundUsers: data.data.map((user) => user.user) })),
            catchError(error => of(UsersActions.usersError({ error: error.message })))
          )
      )
    )
  );

  searchUserRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.searchUserRoles),
      map(action => action),
      switchMap(action =>
        this.usersService.searchUserRoles().pipe(
          map(userRoles => UsersActions.searchUserRolesSuccess({ userRoles })),
          catchError(error => of(UsersActions.usersError({ error: error.message })))
        )
      )
    )
  );

  searchUsersByQuery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.searchUsersByQuery),
      switchMap(({ request }) =>
        this.httpApiService
          .getRequest(request)
          .pipe(
            map(data => data as ResponseModel),
            map(payload => payload.data),
            map(foundUsers => UsersActions.searchUsersByQuerySuccess({ foundUsers })),
            catchError(error => of(UsersActions.usersError({ error: error.message })))
          )
      )
    )
  );

  addRoleToUserToBusiness$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.addRoleToUserToBusiness),
      map(action => action.payload),
      switchMap(payload =>
        this.usersService
          .addRoleToUserToBusiness(payload)
          .pipe(
            map(user => UsersActions.addRoleToUserToBusinessSuccess({ user })),
            catchError(error => of(UsersActions.usersError({ error: error.message })))
          )
      )
    )
  );

  addRoleToUserToReseller$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.addRoleToUserToReseller),
      map(action => action.request),
      switchMap(request =>
        this.httpApiService
          .postRequest(request)
          .pipe(
            map(data => data as UserBusinessModel),
            map(user => UsersActions.addRoleToUserToResellerSuccess({ user })),
            catchError(error => of(UsersActions.usersError({ error: error.message })))
          )
      )
    )
  );

  createSentInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.createSentInvitation),
      map(action => action.sentInvitation),
      withLatestFrom(
        this.store.select(fromApp.getSelectedBusinessProfile),
        this.store.select(fromApp.getLoggedUser),
        (invitation: SentInvitationModel, business: BusinessModel, user: UserModel) => ({
          ...invitation,
          businessId: business.id,
          senderEmail: user.email,
          resellerId: business.resellerId,
        })
      ),
      switchMap(sentInvitation =>
        this.usersService
          .createSentInvitation(sentInvitation)
          .pipe(
            map(sentInvitation => UsersActions.createSentInvitationSuccess({ sentInvitation })),
            catchError(error => of(UsersActions.usersError({ error: error.message })))
          )
      )
    )
  );

  createSentInvitationToReseller$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.createSentInvitationToReseller),
      map(action => action.request),
      withLatestFrom(
        this.store.select(fromApp.getSelectedResellerProfile),
        this.store.select(fromApp.getLoggedUser),
        (request: RequestModel, reseller: ResellerModel, user: UserModel) => ({
          ...request,
          payload: { ...request.payload, resellerId: reseller.id, senderEmail: user.email }
        })
      ),
      switchMap(request =>
        this.httpApiService
          .postRequest(request)
          .pipe(
            map(data => data as UserBusinessModel),
            map(sentInvitation => UsersActions.createSentInvitationSuccess({ sentInvitation })),
            catchError(error => of(UsersActions.usersError({ error: error.message })))
          )
      )
    )
  );

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.deleteUser),
      map(action => ({ userId: action.userId })),
      withLatestFrom(
        this.store.select(fromApp.getSelectedBusinessProfile),
        (payload: { userId: string }, business: BusinessModel) => ({
          ...payload,
          businessId: business.id
        })
      ),
      switchMap(payload =>
        this.usersService
          .deleteUser(payload)
          .pipe(
            tap(data => this.launchToastMssg('success', 'Delete User', 'The user has been deleted')),
            map(userId => UsersActions.deleteUserSucces({ userId })),
            catchError(error => of(UsersActions.usersError({ error: error.message })))
          )
      )
    )
  );

  deleteUserFromReseller$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.deleteUserFromReseller),
      map(action => action.request),
      switchMap(request =>
        this.httpApiService
          .postRequest(request)
          .pipe(
            map((data: { data: string }) => data.data as string),
            tap(data => this.launchToastMssg('success', 'Delete User', 'The user has been deleted from reseller')),
            map(userId => UsersActions.deleteUserFromResellerSuccess({ userId })),
            catchError(error => of(UsersActions.usersError({ error: error.message })))
          )
      )
    )
  );

  resendSentInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.resendSentInvitation),
      map(action => action.payload),
      switchMap(payload =>
        this.usersService
          .resendSentInvitation(payload)
          .pipe(
            tap(sentInvitation => this.launchToastMssg('success', 'Resend Invitation', 'The invitation has been forwarded')),
            map(sentInvitation => UsersActions.resendSentInvitationSuccess({ sentInvitation })),
            catchError(error => of(UsersActions.usersError({ error: error.message })))
          )
      )
    )
  )

  searchAllUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.searchAllUsers),
      switchMap(({ request }) =>
        this.httpApiService.getRequest(request)
          .pipe(
            map(data => data as ResponseModel),
            map(users => UsersActions.searchAllUsersSuccess({ users })),
            catchError(error => of(UsersActions.usersError({ error: error.message })))
          )
      )
    )
  );

  searchUsersByReseller$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.searchUsersByReseller),
      switchMap(({ request }) =>
        this.httpApiService
          .getRequest(request)
          .pipe(
            map(data => data as ResponseModel),
            map(users => UsersActions.searchAllUsersSuccess({ users })),
            catchError(error => of(UsersActions.usersError({ error: error.message })))
          )
      )
    )
  );

  editUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.editUser),
      switchMap(({ request }) =>
        this.httpApiService
          .postRequest(request)
          .pipe(
            map(data => data as UserModel),
            tap(user => this.launchToastMssg('success', 'Edit User', `User ${user.firstName} ${user.lastName} has been edited`)),
            map(user => UsersActions.editUserSuccess({ user })),
            catchError(error => of(UsersActions.usersError({ error: error.message })))
          )
      )
    )
  );

  saveUserPhoneNumber$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.saveUserPhoneNumber),
      switchMap(({ request }) =>
        this.httpApiService
          .postRequest(request)
          .pipe(
            map(data => data as UserModel),
            tap(user => this.launchToastMssg('success', 'Review notification', `Mobile notification has been saved`)),
            map(user => UsersActions.saveUserPhoneNumberSuccess({ user })),
            catchError(error => of(UsersActions.usersError({ error: error.message })))
          )
      )
    )
  );

  updateShowModalPhoneNumber$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.updateShowModalPhoneNumber),
      switchMap(({ request }) =>
        this.httpApiService
          .postRequest(request)
          .pipe(
            map(data => data as UserModel),
            map(user => UsersActions.updateShowModalPhoneNumberSuccess({ user })),
            catchError(error => of(UsersActions.usersError({ error: error.message })))
          )
      )
    )
  );
  updateRoleOnReseller$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.updateRoleOnResellerView),
      switchMap(({ request }) =>
        this.httpApiService
          .postRequest(request)
          .pipe(
            map(data => data as UserModel),
            tap(user => this.launchToastMssg('success', 'Edit User', `User ${user.firstName} ${user.lastName} has been edited`)),
            mergeMap(user => [
              UsersActions.editUserSuccess({ user }),
              UsersActions.searchUsersByReseller({
                request: {
                  params: `resellers/users?id=${request.payload.resellerId}&page=0&size=10`
                }
              }),
            ]),
            catchError(error => {
              this.launchToastMssg('error', error.error.message, 'Error');
              return of(UsersActions.usersError({ error: error.message }))
            })
          )
      )
    )
  );
  updateRoleOnBusinessView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.updateRoleOnBusinessView),
      switchMap(({ request }) =>
        this.httpApiService
          .postRequest(request)
          .pipe(
            map(data => data as UserModel),
            tap(user => this.launchToastMssg('success', 'Edit User', `User ${user.firstName} ${user.lastName} has been edited`)),
            mergeMap(user => [
              UsersActions.editUserSuccess({ user }),
              UsersActions.searchUsersByBusiness({
                request: {
                  params: `userBusiness/business?id=${request.payload.businessId}&page=0&size=10`,
                }
              }),
            ]),
            catchError(error => {
              this.launchToastMssg('error', error.error.message, 'Error');
              return of(UsersActions.usersError({ error: error.message }))
            })
          )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private usersService: UsersService,
    private httpApiService: HttpApiService,
    private messageToast: MessageService,
    private store: Store<any>
  ) { }

  launchToastMssg(severity: string, summary: string, detail: string) {
    this.messageToast.add({ severity: severity, summary: summary, detail: detail, life: 5000 });
  }
}
